'use client'

import type { Marble } from 'src/types/domain/Marble'
// The glyph vocabulary (`.birdie-glyph`, the ball, the head icon) lives in the
// board's stylesheet; this one adds only the feed's own slot around it.
import './birdie-board.scss'
import './birdie-feed.scss'
import { BoardHeadIcon } from './BoardHeadIcon'
import { PuttGlyph } from './PuttGlyph'
import { RoundMark } from './RoundMark'
import { familyForKey, glyphsForKey } from './tileViewModel'

/**
 * The feed row's balls, px. `.social-bet-table__entry-picks .marble` reserves a
 * 15-px box for every other game's pick, so the Birdie pick takes the same one
 * and the rows do not move.
 */
export const FEED_PICK_BALL_PX = 15

/**
 * The round-type mark's box in a feed row. Larger than a ball because the mark is the whole pick —
 * there are no glyphs beside it — and it still sits inside the 15-px box every other game's pick
 * reserves, so no row moves.
 */
export const FEED_PICK_MARK_PX = 17

type Props = Readonly<{
  /**
   * The selected option, as the panel resolved it: one of Birdie's twelve
   * `game.marbles` rows, whose `color` is the canonical key (`IN2`, `HMH`).
   */
  marble: Pick<Marble, 'color'>
}>

/**
 * One Birdie pick in the live-bets panel (B22): the family icon, then the balls
 * the player backed, in the board's own vocabulary — three balls in putt order
 * for an EXACT pick, the holed count for a COUNT pick (`IN0` keeps its three
 * misses; see `glyphsForKey`).
 *
 * WHY. `<BetMarbles>` draws a pick by skinning the selection as a marble, and a
 * Birdie option has no marble drawing behind it — its colour is a key — so the
 * row showed a grey token that said nothing about what was backed (owner: "it's
 * a bit hard to see their picked option"). The key is read off the row exactly
 * as `BirdieBetHistoryRows` reads it (`marble.color.toUpperCase()`), through the
 * SAME `familyForKey` / `glyphsForKey` the board tiles draw from, so a tile, a
 * history line and a feed row can never disagree about one option.
 *
 * A key that resolves to neither family renders NOTHING — never a token. The
 * balls' gradients are declared once per panel by `<BirdieBallDefs>` (fixed
 * ids, idempotent — `GolfBall.tsx`); this component draws none of its own.
 *
 * Decorative throughout: the icon and the glyphs are `aria-hidden` on their
 * own, and the panel row carries no accessible name for a pick on any game.
 */
export function BirdieFeedPick({ marble }: Props) {
  const key = marble.color.toUpperCase()
  const family = familyForKey(key)
  const marks = glyphsForKey(key)
  if (family === undefined || marks === undefined) return null
  // B36: a round-type pick draws its own mark and no balls, because it describes no putts. It must
  // never borrow the any-order icon: these settle off the round's seed, not off the golfer, and a
  // feed row that implied otherwise would be the one place a player learns the wrong thing about a
  // market they just backed.
  if (family === 'round') {
    return (
      <span aria-hidden="true" className="birdie-feed__pick birdie-feed__pick--round">
        <span className={`birdie-feed__fam birdie-feed__fam--${key.toLowerCase()}`}>
          <RoundMark kind={key === 'FIRE' ? 'FIRE' : 'FROST'} size={FEED_PICK_MARK_PX} />
        </span>
      </span>
    )
  }

  return (
    <span aria-hidden="true" className="birdie-feed__pick">
      <span className="birdie-feed__fam">
        <BoardHeadIcon column={family} />
      </span>
      <span className="birdie-feed__balls">
        {marks.map((mark, index) => (
          // Fixed length, immutable order: a placed bet's picked pattern cannot
          // change under the key.
          // eslint-disable-next-line react/no-array-index-key -- fixed-length, immutable putt order
          <PuttGlyph key={index} mark={mark} size={FEED_PICK_BALL_PX} />
        ))}
      </span>
    </span>
  )
}
